// src/services/admin.portfolio.service.js
import { apiClient } from "@/config/api.config";

/**
 * Service to handle all portfolio and holdings-related API calls for admin
 */
class AdminPortfolioService {
  // ============================================================================
  // PORTFOLIO METHODS
  // ============================================================================

  /**
   * Fetch all portfolios with optional filtering, sorting, and pagination
   * @param {Object} params - Query parameters for filtering and pagination
   * @returns {Promise} - Promise with portfolio data
   */
  async getPortfolios(params = {}) {
    try {
      const response = await apiClient.get(`/admin/portfolios`, { params });
      return response.data;
    } catch (error) {
      throw this._handleError(error);
    }
  }

  /**
   * Fetch a specific portfolio by ID
   * @param {string} portfolioId - Portfolio ID
   * @returns {Promise} - Promise with portfolio details
   */
  async getPortfolioById(portfolioId) {
    try {
      const response = await apiClient.get(`/admin/portfolios/${portfolioId}`);
      return response.data;
    } catch (error) {
      throw this._handleError(error);
    }
  }

  /**
   * Approve or reject portfolio rebalance request
   * @param {string} portfolioId - Portfolio ID
   * @param {Object} data - Approval data
   * @returns {Promise} - Promise with response data
   */
  async approveRebalanceRequest(portfolioId, data) {
    try {
      const response = await apiClient.patch(
        `/admin/portfolios/${portfolioId}/rebalance`,
        data
      );
      return response.data;
    } catch (error) {
      throw this._handleError(error);
    }
  }

  // ============================================================================
  // HOLDINGS METHODS
  // ============================================================================

  async getAllHoldings(params = {}) {
    try {
      const response = await apiClient.get(`/admin/allholdings`, {
        params,
      });
      return response.data;
    } catch (error) {
      throw this._handleError(error);
    }
  }

  /**
   * Fetch holdings statistics for admin dashboard
   * @returns {Promise} - Promise with holdings statistics
   */
  async getHoldingsStats() {
    try {
      const response = await apiClient.get(`/admin/holdings/stats`);
      return response.data;
    } catch (error) {
      throw this._handleError(error);
    }
  }

  /**
   * Fetch detailed information for a specific holding
   * @param {string} holdingId - Holding ID
   * @returns {Promise} - Promise with holding details
   */
  async getHoldingDetails(holdingId) {
    try {
      const response = await apiClient.get(`/admin/holdings/${holdingId}`);
      return response.data;
    } catch (error) {
      throw this._handleError(error);
    }
  }

  /**
   * Mark specific holding as read or unread by admin
   * @param {string} holdingId - Holding ID
   * @param {boolean} [isRead=true] - Mark as read (true) or unread (false)
   * @returns {Promise} - Promise with response data
   */
  async markHoldingAsRead(holdingId, isRead = true) {
    try {
      const response = await apiClient.patch(
        `/admin/holdings/${holdingId}/read`,
        { isRead }
      );
      return response.data;
    } catch (error) {
      throw this._handleError(error);
    }
  }

  /**
   * Mark multiple holdings as read or unread
   * @param {string[]} holdingIds - Array of holding IDs
   * @param {boolean} [isRead=true] - Mark as read (true) or unread (false)
   * @returns {Promise} - Promise with response data
   */
  async markMultipleHoldingsAsRead(holdingIds, isRead = true) {
    try {
      const response = await apiClient.patch(`/admin/holdings/bulk-read`, {
        holdingIds,
        isRead,
      });
      return response.data;
    } catch (error) {
      throw this._handleError(error);
    }
  }

  // ============================================================================
  // RECOMMENDATIONS METHODS
  // ============================================================================

  /**
   * Fetch all investment recommendations
   * @param {Object} params - Query parameters for filtering and pagination
   * @returns {Promise} - Promise with recommendations data
   */
  async getAllRecommendations(params = {}) {
    try {
      const response = await apiClient.get(`/admin/recommendations`, {
        params,
      });
      return response.data;
    } catch (error) {
      throw this._handleError(error);
    }
  }

  // ============================================================================
  // UTILITY METHODS
  // ============================================================================

  /**
   * Get unread holdings count (for dashboard badge)
   * @returns {Promise} - Promise with unread count
   */
  async getUnreadHoldingsCount() {
    try {
      const response = await this.getAllHoldings({
        isRead: false,
        page: 1,
        limit: 1,
      });
      return response.data?.pagination?.total || 0;
    } catch (error) {
      return 0;
    }
  }

  /**
   * Get recent holdings (for dashboard)
   * @param {number} [limit=10] - Number of recent holdings to fetch
   * @returns {Promise} - Promise with recent holdings
   */
  async getRecentHoldings(limit = 10) {
    try {
      const response = await this.getAllHoldings({
        sortBy: "createdAt",
        sortOrder: "desc",
        page: 1,
        limit,
      });
      return response.data?.holdings || [];
    } catch (error) {
      throw this._handleError(error);
    }
  }

  /**
   * Get flagged holdings (holdings that need admin attention)
   * @param {Object} params - Query parameters
   * @returns {Promise} - Promise with flagged holdings
   */
  async getFlaggedHoldings(params = {}) {
    try {
      const response = await apiClient.get(`/admin/holdings`, {
        params: {
          ...params,
          flagged: true,
          sortBy: "createdAt",
          sortOrder: "desc",
        },
      });
      return response.data;
    } catch (error) {
      throw this._handleError(error);
    }
  }

  // ============================================================================
  // BATCH OPERATIONS
  // ============================================================================

  /**
   * Perform batch operations on holdings
   * @param {Object} batchData - Batch operation data
   * @param {string[]} batchData.holdingIds - Array of holding IDs
   * @param {string} batchData.operation - Operation type ('markRead', 'markUnread', 'flag', 'unflag')
   * @param {Object} [batchData.additionalData] - Additional data for the operation
   * @returns {Promise} - Promise with batch operation result
   */
  async performBatchOperation(batchData) {
    try {
      const { holdingIds, operation, additionalData = {} } = batchData;

      switch (operation) {
        case "markRead":
          return await this.markMultipleHoldingsAsRead(holdingIds, true);

        case "markUnread":
          return await this.markMultipleHoldingsAsRead(holdingIds, false);

        case "flag":
          const response = await apiClient.patch(`/admin/holdings/bulk-flag`, {
            holdingIds,
            flagged: true,
            ...additionalData,
          });
          return response.data;

        case "unflag":
          const unflagResponse = await apiClient.patch(
            `/admin/holdings/bulk-flag`,
            {
              holdingIds,
              flagged: false,
            }
          );
          return unflagResponse.data;

        default:
          throw new Error(`Unknown batch operation: ${operation}`);
      }
    } catch (error) {
      throw this._handleError(error);
    }
  }

  // ============================================================================
  // PRIVATE METHODS
  // ============================================================================

  /**
   * Error handler
   * @private
   * @param {Error} error - Error object
   * @returns {Error} - Processed error
   */
  _handleError(error) {
    if (error.response) {
      // The request was made and the server responded with a status code
      // that falls out of the range of 2xx
      return {
        message: error.response.data.message || "An error occurred",
        status: error.response.status,
        data: error.response.data,
      };
    } else if (error.request) {
      // The request was made but no response was received
      return {
        message: "No response received from server",
        status: 0,
      };
    } else {
      // Something happened in setting up the request that triggered an Error
      return {
        message: error.message,
        status: 0,
      };
    }
  }
}

export const adminPortfolioService = new AdminPortfolioService();
export default adminPortfolioService;
